<template>
  <div>
    <h2 class="mb-4 text-center">Check the Status of Your FOIA Request</h2>

    <ContentCard>
      <template v-if="!statusData">
        <p class="lead">Please enter the {{ idName }} and Personal Identification Number (PIN)
          that you received in your acknowledgement letter.</p>

        <CompleteFormMessage class="mb-4" />

        <div class="d-flex flex-column gap-4">
          <div class="row g-3">
            <TextBox
              :label=idName
              class="col-md-6"
              :mask="{ mask: '99999999*999', placeholder: '' }"
              :rules="{
                length: length(12),
                required
              }"
              v-model="formData.controlNumber"
            />

            <TextBox
              label="PIN"
              class="col-md-6"
              :mask="{ mask: '****', placeholder: '' }"
              :rules="{
                length: length(4),
                required
              }"
              v-model="formData.pin"
            />
          </div>

          <button class="btn btn-accent-warm btn-lg btn-thick btn-grow w-100"
            :disabled="!isValid"
            @click="showSubmitModal = true"
          >Check Status</button>
        </div>
      </template>
      <template v-else>
        <table>
          <tr v-for="([label, value]) in statusFields" :key="label">
            <td class="form-label text-end align-top pe-2 py-2"
              style="width: 15em;"
            >{{ label }}:</td>
            <td class="py-2">{{ value }}</td>
          </tr>
        </table>
        <slot name="footer"></slot>
      </template>
    </ContentCard>

    <SubmitFormModal
      :can-retry-failure="!notFound"
      :show="showSubmitModal"
      :submit-fn="submit"
      @close="showSubmitModal = false"
      @success="showSubmitModal = false"
    >
      <template #error-body v-if="notFound">
        <slot name="not-found-error"></slot>
      </template>
    </SubmitFormModal>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import CompleteFormMessage from '@/components/formFields/CompleteFormMessage';
import SubmitFormModal from '@/components/Modals/SubmitFormModal';
import { TextBox } from '@/components/formFields/common';
import { checkStatus } from '@/api';
import NotFoundError from '@/api/errors/notFound';
import { length, required } from '@/validators';

export default {
  setup() {
    const v$ = useVuelidate();

    return {
      length,
      required,
      v$
    };
  },

  props: {
    idName: {
      type: String,
      default: 'Tracking Number'
    },

    getStatusFields: {
      type: Function,
      required: true
    }
  },

  components: {
    CompleteFormMessage, SubmitFormModal, TextBox
  },

  data: () => ({
    formData: {
      controlNumber: null,
      pin: null,
    },
    notFound: false,
    statusData: null,
    showSubmitModal: false
  }),

  computed: {
    // See https://github.com/vuelidate/vuelidate/issues/764 for why a computed prop is necessary
    // instead of using v$.$invalid directly in the template.
    isValid: ({ v$ }) => !v$.$invalid,

    statusFields: ({ getStatusFields, statusData }) => getStatusFields(statusData)
  },

  methods: {
    submit(captchaToken) {
      this.notFound = false;

      return checkStatus(this.formData, captchaToken)
        .catch((e) => {
          if (e instanceof NotFoundError) {
            this.notFound = true;
          }

          throw e;
        })
        .then((result) => {
          this.showSubmitModal = false;
          this.statusData = result;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/animations";
</style>
